import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { GraduationCap, CalendarRange, ChevronDown } from "lucide-react";
import { useUiState } from "@/lib/uiState";

const STATUS_STYLES = {
  current: "bg-emerald-100 text-emerald-800", retired: "bg-sky-100 text-sky-800",
  emeritus: "bg-amber-100 text-amber-800", deceased: "bg-slate-200 text-slate-700",
};
const tenure = (s) => {
  if (s.year_start == null && s.year_end == null) return "";
  return `${s.year_start ?? "?"}–${s.year_end == null ? "present" : s.year_end}`;
};
const Initials = ({ name }) => <span className="text-xs font-semibold text-primary">{(name || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase()}</span>;

export default function ClassFaculty({ year }) {
  const [faculty, setFaculty] = useState(null);
  const [open, setOpen] = useUiState("ud_class_faculty_open", true);
  const navigate = useNavigate();

  useEffect(() => {
    setFaculty(null);
    api.get(`/experience/class-faculty?year=${year}`).then((r) => setFaculty(r.data.faculty)).catch(() => setFaculty([]));
  }, [year]);

  if (!faculty || faculty.length === 0) return null;

  return (
    <section className="mb-6 bg-accent/[0.06] border border-accent/25 rounded-xl p-4 sm:p-5" data-testid="class-faculty">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-2 text-left" data-testid="class-faculty-toggle">
        <div className="flex items-center gap-2">
          <GraduationCap className="h-5 w-5 text-primary" />
          <h2 className="font-serif text-xl tracking-tight">Professors who taught the Class of {year}</h2>
          <span className="text-xs text-muted-foreground">({faculty.length})</span>
        </div>
        <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <>
          <p className="text-xs text-muted-foreground mt-1 mb-3">Faculty &amp; staff who were on campus while your class was here. <button onClick={() => navigate("/experience/faculty")} className="text-primary hover:underline">See all faculty</button></p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {faculty.map((m) => (
              <div key={m.id} data-testid={`class-faculty-${m.id}`} className="flex items-center gap-3 bg-card border border-border/70 rounded-lg p-2.5">
                <div className={`h-10 w-10 rounded-full bg-accent/20 flex items-center justify-center overflow-hidden border border-border shrink-0 ${m.status === "deceased" ? "grayscale" : ""}`}>
                  {m.photo ? <img src={m.photo} alt={m.name} className="h-full w-full object-cover" loading="lazy" /> : <Initials name={m.name} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground truncate">{m.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{[m.title, m.department].filter(Boolean).join(" · ") || tenure(m)}</div>
                </div>
                {m.status && m.status !== "current" && <span className={`text-[9px] font-mono uppercase px-1.5 py-0.5 rounded shrink-0 ${STATUS_STYLES[m.status] || ""}`}>{m.status}</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
